import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@/components/ui/tooltip';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { MousePointerClick, Users, ArrowUpRight, Phone, Info, TrendingUp, Lightbulb, Plus } from 'lucide-react';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip as RechartsTooltip, ResponsiveContainer, Legend } from 'recharts';
import CampaignStatusTracker from '@/components/CampaignStatusTracker';
import { useNavigate } from 'react-router-dom';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DashboardStart = ({ userData, onNavigate }) => {
  const navigate = useNavigate();
  const [stats, setStats] = useState({
    clicks: 0,
    anfragen: 0,
    conversions: 0,
    conversionRate: 0,
    kosten: 0,
    kostenProAnfrage: 0,
  });
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [openTooltips, setOpenTooltips] = useState({});
  
  // Callback Modal States
  const [callbackModalOpen, setCallbackModalOpen] = useState(false);
  const [callbackLoading, setCallbackLoading] = useState(false);
  const [callbackDate, setCallbackDate] = useState('');
  const [callbackTime, setCallbackTime] = useState('');
  const [callbackPhone, setCallbackPhone] = useState('');
  const [callbackNote, setCallbackNote] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  // Get tomorrow's date as minimum
  const tomorrow = new Date();
  tomorrow.setDate(tomorrow.getDate() + 1);
  const tomorrowStr = tomorrow.toISOString().split('T')[0];

  const handleRequestCallback = async () => {
    if (!callbackDate || !callbackTime || !callbackPhone) {
      toast.error('Bitte füllen Sie alle Pflichtfelder aus');
      return;
    }

    // Validate phone number
    const phoneRegex = /^[\d\s\+\-\(\)]+$/;
    if (!phoneRegex.test(callbackPhone)) {
      toast.error('Bitte geben Sie eine gültige Telefonnummer ein');
      return;
    }

    setCallbackLoading(true);
    try {
      const token = localStorage.getItem('token');
      await axios.post(
        `${API}/support/callbacks`,
        {
          preferred_date: callbackDate,
          preferred_time: callbackTime,
          phone_number: callbackPhone,
          note: callbackNote || null,
        },
        {
          headers: { Authorization: `Bearer ${token}` }
        }
      );

      toast.success('Rückruf erfolgreich angefordert!', {
        description: 'Wir melden uns verlässlich zum gewünschten Zeitpunkt bei Ihnen.',
      });
      
      // Reset form and close modal
      setCallbackDate('');
      setCallbackTime('');
      setCallbackPhone('');
      setCallbackNote('');
      setCallbackModalOpen(false);
    } catch (error) {
      console.error('Error requesting callback:', error);
      toast.error('Fehler beim Anfordern des Rückrufs');
    } finally {
      setCallbackLoading(false);
    }
  };

  const fetchDashboardData = async () => {
    try {
      const token = localStorage.getItem('token');
      
      // Fetch aggregated KPIs from new endpoint
      const kpisResponse = await axios.get(`${API}/dashboard/kpis?days=30`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      const kpis = kpisResponse.data;
      
      setStats({
        clicks: kpis.total_clicks || 0,
        anfragen: kpis.total_conversions || 0,
        conversions: kpis.total_conversions || 0,
        conversionRate: kpis.conversion_rate || 0,
        kosten: kpis.total_cost || 0,
        kostenProAnfrage: kpis.cost_per_conversion || 0,
      });

      // Format chart data
      const chartArray = (kpis.daily_stats || []).map(day => ({
        date: new Date(day.date).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit' }),
        klicks: day.clicks || 0,
        anfragen: day.conversions || 0,
        kosten: day.cost || 0
      }));

      setChartData(chartArray);
    } catch (error) {
      console.error('Error fetching dashboard data:', error);
      toast.error('Fehler beim Laden der Dashboard-Daten');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#0040FF] mx-auto mb-4"></div>
          <p className="text-gray-600">Daten werden geladen...</p>
        </div>
      </div>
    );
  }

  const displayName = userData?.company_name || userData?.email?.split('@')[0] || 'User';

  return (
    <div className="space-y-8">
      {/* Header */}
      <div>
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          Willkommen zurück, {displayName}
        </h1>
        <p className="text-gray-600">
          Hier ist eine Übersicht Ihrer Kampagnen-Performance der letzten 30 Tage.
        </p>
      </div>

      {/* Campaign Status Tracker */}
      <CampaignStatusTracker onNavigate={onNavigate} />

      {/* Performance Summary Text - Only show if user has campaign data */}
      {(stats.clicks > 0 || stats.anfragen > 0 || stats.kosten > 0) && (
        <Card className="bg-gradient-to-br from-blue-50 to-indigo-50 border-blue-200">
          <CardContent className="pt-6">
            <div className="flex items-start gap-4">
              <div className="w-12 h-12 rounded-full bg-[#0040FF] flex items-center justify-center flex-shrink-0">
                <TrendingUp className="w-6 h-6 text-white" />
              </div>
              <div>
                <h3 className="font-semibold text-gray-900 mb-2">Performance-Zusammenfassung</h3>
                <p className="text-gray-700 leading-relaxed">
                  Großartig! In den letzten 30 Tagen konnten wir <strong className="text-[#0040FF]">{stats.clicks.toLocaleString('de-DE')}</strong> Klicks 
                  und <strong className="text-[#0040FF]">{stats.anfragen.toLocaleString('de-DE')}</strong> wertvolle Anfragen für Sie generieren. 
                  Dabei haben Sie insgesamt <strong className="text-[#0040FF]">€{stats.kosten.toFixed(2)}</strong> investiert, 
                  was zu durchschnittlichen Kosten von <strong className="text-[#0040FF]">
                  €{stats.kostenProAnfrage > 0 ? stats.kostenProAnfrage.toFixed(2) : '0.00'}</strong> pro Anfrage führt. 
                  Unser Expertenteam arbeitet kontinuierlich daran, diese Ergebnisse für Sie zu optimieren und noch bessere Performance zu erzielen! 🚀
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      )}

      {/* Expertenstatus & Hilfe & Neue Kampagne */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
        {/* Expertenstatus */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              Experten-Optimierung
              <Badge variant="success" className="flex items-center gap-1">
                <div className="w-2 h-2 bg-green-600 rounded-full animate-pulse"></div>
                Aktiv
              </Badge>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div>
              <p className="text-sm text-gray-600 mb-1">Letzte Optimierung</p>
              <p className="text-base font-medium text-gray-900">Vor 3 Tagen</p>
            </div>
            <div>
              <p className="text-sm text-gray-600 mb-1">Nächste Optimierung</p>
              <p className="text-base font-medium text-gray-900">In 4 Tagen</p>
            </div>
            <div className="pt-2">
              <p className="text-sm text-gray-500">
                Unsere Experten optimieren Ihre Kampagnen kontinuierlich für maximale Performance.
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Hilfe Box */}
        <Card className="bg-gradient-to-br from-[#0040FF] to-[#0030CC] text-white border-0">
          <CardHeader>
            <CardTitle className="text-white">Brauchen Sie Unterstützung?</CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-white/90 text-sm">
              Unser Team steht Ihnen jederzeit zur Verfügung. Kontaktieren Sie uns per Telefon, E-Mail oder fordern Sie einen Rückruf an.
            </p>
            <Button
              className="w-full bg-white text-[#0040FF] hover:bg-gray-100"
              onClick={() => setCallbackModalOpen(true)}
            >
              <Phone className="w-4 h-4 mr-2" />
              Rückruf anfordern
            </Button>
          </CardContent>
        </Card>

        {/* Tipp vom Experten - Neue Kampagne */}
        <Card className="border-2 border-dashed border-gray-300 hover:border-[#0040FF] transition-colors">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-gray-900">
              <Lightbulb className="w-5 h-5 text-amber-500" />
              Tipp vom Experten
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <p className="text-sm text-gray-600">
              Spezialisierte Anzeigen für einzelne Dienstleistungen bringen durchschnittlich <strong className="text-[#0040FF]">40% mehr Anfragen</strong>.
            </p>
            <Button
              className="w-full bg-[#0040FF] hover:bg-[#0033CC] text-white"
              onClick={() => navigate('/new-campaign')}
            >
              <Plus className="w-4 h-4 mr-2" />
              Neue Kampagne starten
            </Button>
          </CardContent>
        </Card>
      </div>

      {/* Callback Request Modal */}
      <Dialog open={callbackModalOpen} onOpenChange={setCallbackModalOpen}>
        <DialogContent className="sm:max-w-[500px]">
          <DialogHeader>
            <DialogTitle>Rückruf anfordern</DialogTitle>
            <DialogDescription>
              Wir melden uns zum gewünschten Zeitpunkt bei Ihnen.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Phone Number */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Telefonnummer *
              </label>
              <Input
                type="tel"
                placeholder="+43 1 234 5678"
                value={callbackPhone}
                onChange={(e) => setCallbackPhone(e.target.value)}
                className="h-11"
              />
            </div>

            {/* Date */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Wunschdatum *
              </label>
              <Input
                type="date"
                min={tomorrowStr}
                value={callbackDate}
                onChange={(e) => setCallbackDate(e.target.value)}
                className="h-11"
              />
            </div>

            {/* Time */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Wunschuhrzeit *
              </label>
              <Input
                type="time"
                value={callbackTime}
                onChange={(e) => setCallbackTime(e.target.value)}
                className="h-11"
              />
            </div>

            {/* Note (optional) */}
            <div className="space-y-2">
              <label className="text-sm font-medium text-gray-700">
                Nachricht (optional)
              </label>
              <Textarea
                placeholder="Ihr Anliegen..."
                value={callbackNote}
                onChange={(e) => setCallbackNote(e.target.value)}
                rows={3}
              />
            </div>
          </div>

          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setCallbackModalOpen(false)}
              disabled={callbackLoading}
            >
              Abbrechen
            </Button>
            <Button
              onClick={handleRequestCallback}
              disabled={callbackLoading}
              className="bg-[#0040FF] hover:bg-[#0033CC]"
            >
              {callbackLoading ? 'Wird gesendet...' : 'Rückruf anfordern'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default DashboardStart;
